import React, { FC, useEffect, useState } from 'react';
import { Titled } from 'react-titled';
import { useTranslation } from 'react-i18next';
import AppLayout from 'components/common/AppLayout';
import PageLoading from 'components/common/PageLoading';
import { Flex, Text, Box } from '@resideo/blueprint-react';
import FirstAlertLogo from 'components/logos/FirstAlertLogo.png';
import ResideoAcademyLogo from 'components/logos/ResideoAcademyLogo.png';
import ResideoProAppLogo from 'components/logos/ResideoProAppLogo.png';
import ResideoTechAppLogo from 'components/logos/ResideoTechAppLogo.png';
import useTokenClaims from 'hooks/useTokenClaims';
import { useFeatureFlags } from 'context/FeatureFlags';
import { LOGIN_CUSTOM_ENDPOINTS, PRO_PORTAL_URL } from 'config';
import NoticeLayoutRedesign from 'components/common/NoticeLayoutRedesign';
import { useNavigate } from 'react-router-dom';
import { AppNamesEnum } from 'types/enums/AppNames';
import { useQuery } from '@tanstack/react-query';
import { useRisClient } from 'hooks/useRisClient';
import styled from 'styled-components';
import AppStoreIcon from 'components/icons/appStore.svg?react';
import GooglePlayIcon from 'components/icons/googlePlay.svg?react';
import DownloadTheApp from '../DownloadTheApp';

const Container = styled.div`
  height: 100%;
  display: grid;
  justify-content: center;
  align-items: center;
`;

const ImageContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
`;

const DownloadContainer = styled(Flex)`
  margin-bottom: 15px;
  margin-top: 25px;
  display: grid;
  grid-gap: 2rem;
  grid-auto-flow: row;
  justify-content: center;
  width: 500px;
  @media only screen and (min-width: 768px) {
    grid-auto-flow: column;
    width: auto;
  }
`;

const Anchor = styled.a`
  svg {
    width: 260px;
    height: 80px;
  }
  @media only screen and (min-width: 768px) {
    svg {
      width: 356px;
      height: 110px;
    }
  }
`;

const PostVerification: FC = (): JSX.Element => {
  const {
    isLoading,
    userType,
    isPartner,
    returnToUrl,
    signUpApp,
    signUpPartnerGuest,
  } = useTokenClaims();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { RESIDEO_ACADEMY_SPLASH_PAGE, RESIDEO_TECH_APP } = useFeatureFlags();
  // To prevent flashing the normal UI while redirecting
  const [redirecting, setRedirecting] = useState(true);
  const { client } = useRisClient();

  const descriptionMap = {
    [AppNamesEnum.PROAPP]: t(
      RESIDEO_TECH_APP
        ? 'signUp.consumerWelcome.descriptionTechApp'
        : 'signUp.consumerWelcome.descriptionProApp'
    ),
    [AppNamesEnum.RESIDEOACADEMY]: t(
      'signUp.consumerWelcome.descriptionResideoAcademy'
    ),
  };

  const logoMap = {
    [AppNamesEnum.PROAPP]: RESIDEO_TECH_APP ? (
      <img
        src={ResideoTechAppLogo}
        alt={t('signUp.consumerWelcome.logoAltTechApp')}
        width={96}
        height={96}
      />
    ) : (
      <img
        src={ResideoProAppLogo}
        alt={t('signUp.consumerWelcome.logoAltProApp')}
      />
    ),
    [AppNamesEnum.RESIDEOACADEMY]: (
      <img
        src={ResideoAcademyLogo}
        alt={t('signUp.consumerWelcome.logoAltResideoAcademy')}
      />
    ),
  };

  const isSignUpApp = Object.values(AppNamesEnum).includes(
    signUpApp as AppNamesEnum
  );
  const isSignUpPartnerGuest = Object.values(AppNamesEnum).includes(
    signUpPartnerGuest as AppNamesEnum
  );

  const isExternalSignup = isSignUpApp || isSignUpPartnerGuest;

  const { data: proUserData, isLoading: isProUserDataLoading } = useQuery({
    queryKey: ['proUserData'],
    queryFn: async () =>
      (await client.myProUsers.myProUsersGetMyProUser()).data,
    enabled: isPartner,
    staleTime: 5000,
  });

  const { data: user, isLoading: myUserIsLoading } = useQuery({
    queryKey: ['myUser'],
    queryFn: async () => (await client.myUsers.myUsersGetMyUser()).data,
    enabled: isPartner,
    staleTime: 5000,
  });

  const isDataLoading = isLoading || isProUserDataLoading || myUserIsLoading;

  const hasPartnerAccountOrAddRequest =
    Boolean(proUserData?.partnerUsers?.[0]?.partnerAccount) ||
    Boolean(user?.addRequests?.[0]);

  useEffect(() => {
    if (isDataLoading) {
      return;
    }

    let isRedirect = true;
    if (isPartner && !hasPartnerAccountOrAddRequest) {
      navigate('/sign-up/company-info');
    } else if (isPartner) {
      window.location.href = PRO_PORTAL_URL;
    } else if (returnToUrl) {
      // Does a custom login endpoint's host match redirectUrl's? If so, use that endpoint.
      const customEndPoint = LOGIN_CUSTOM_ENDPOINTS?.find(
        url => new URL(url).host === new URL(returnToUrl).host
      );

      const newUrl = new URL(customEndPoint ?? returnToUrl);

      // Redirect if valid URL
      if (newUrl.protocol === 'http:' || newUrl.protocol === 'https:') {
        window.location.href = newUrl.toString();
      } else if (
        newUrl.protocol === 'sftystage:' ||
        newUrl.protocol === 'sftylive:'
      ) {
        window.location.replace(newUrl.href);
      } else {
        isRedirect = false;
      }
    } else if (hasPartnerAccountOrAddRequest) {
      navigate('/profile');
    } else {
      isRedirect = false;
    }

    setRedirecting(isRedirect);
  }, [
    isLoading,
    userType,
    isProUserDataLoading,
    proUserData,
    user,
    myUserIsLoading,
  ]);

  if (isDataLoading || (isPartner && !isExternalSignup) || redirecting) {
    return <PageLoading />;
  }

  if (!isExternalSignup) {
    return <DownloadTheApp />;
  }

  return (
    <Titled title={title => `${t('welcome')} - ${title}`}>
      <AppLayout showHeader={false} showLeftNav={false} showFooter={false}>
        <NoticeLayoutRedesign
          showFooter={false}
          title={t('signUp.consumerWelcome.header')}>
          <Container>
            <Flex paddingY='large' alignItems='center' justifyContent='center'>
              <Text
                textAlign='center'
                fontWeight='bold'
                fontSize='xLarge'
                lineHeight='1.25'>
                {descriptionMap[isSignUpApp ? signUpApp : signUpPartnerGuest] ||
                  t('signUp.consumerWelcome.description')}
              </Text>
            </Flex>

            {RESIDEO_ACADEMY_SPLASH_PAGE ? (
              <ImageContainer>
                {logoMap[isSignUpApp ? signUpApp : signUpPartnerGuest] || (
                  <img
                    src={FirstAlertLogo}
                    alt={t('signUp.consumerWelcome.logoAltFirstAlert')}
                  />
                )}
                {isSignUpApp && (
                  <DownloadContainer>
                    <Anchor
                      href='https://play.google.com/store/apps/details?id=com.resideo.resideoacademy'
                      target='_blank'
                      rel='noreferrer'>
                      <GooglePlayIcon data-test-google-play-icon />
                    </Anchor>
                    <Anchor
                      href='https://apps.apple.com/us/app/resideo-academy/id6443598287'
                      target='_blank'
                      rel='noreferrer'>
                      <AppStoreIcon data-test-app-store-icon />
                    </Anchor>
                  </DownloadContainer>
                )}
              </ImageContainer>
            ) : (
              <Box marginBottom='xLarge'>
                {logoMap[isSignUpApp ? signUpApp : signUpPartnerGuest] || (
                  <img
                    src={FirstAlertLogo}
                    alt={t('signUp.consumerWelcome.logoAltFirstAlert')}
                  />
                )}
              </Box>
            )}
          </Container>
        </NoticeLayoutRedesign>
      </AppLayout>
    </Titled>
  );
};

export default PostVerification;
